// 術（各キャラ3つ。専用ツリーの3系統に1つずつ）。出撃前に1つ選んで持っていく。
// strike＝相手を狙う術（キャラの基本の術。数値は chars.ts の skill）。それ以外は自分を中心に放つ術（rules.ts の cmdCast）。
// 奥義は持っている術の「奥義版」（ougi の数値）。効果の数値はシミュレーターで調整する（BALANCE.md）
import type { CharId } from '../types';

export type JutsuKind =
  | 'strike' // 相手を狙う（基本の術）
  | 'paint' // 送り火：周りを自陣にして火
  | 'lantern' // 灯籠陣：灯籠の陣（味方の被ダメ減・回復）
  | 'leap' // 縮地：直線に跳んで、着地点の隣を斬る
  | 'gust' // つむじ風：隣の敵を押し出す
  | 'raise' // 突貫普請：周りに櫓・竹柵を一気に建てる
  | 'mend' // 鬼の修繕：建物と本陣を直し、守りを固める
  | 'foxfire' // 狐火の陣：狐火の陣（入った敵が燃える）
  | 'stance'; // 月下の構え：被ダメ減と斬り返し

/** 術の効果の数値（奥義版は ougi に同じ形で） */
export interface JutsuPower {
  /** 範囲（自分から、または選んだマスから何マス）。leap は跳べる距離 */
  radius: number;
  /** 選んだマスに放つ術：自分から何マス先まで選べるか（送り火） */
  range?: number;
  /** ダメージの倍率（攻撃力に対する。0 ならダメージ無し） */
  mult: number;
  /** 陣の続く手番（自分の手番で数える） */
  turns?: number;
  /** 建てる・置く数 */
  count?: number;
  /** 押し出すマス数 */
  push?: number;
  /** 被ダメの倍率（灯籠・構え）。0.85＝-15% */
  taken?: number;
  /** 与ダメの倍率（灯籠陣：自分と陣の中の自分の建物）。1.15＝+15% */
  dealt?: number;
  /** 回復（最大HPの割合） */
  heal?: number;
  /** 建物を直す割合・本陣を直す割合 */
  repair?: number;
  coreRepair?: number;
  /** 斬り返しの倍率（構え） */
  counter?: number;
  /** 相手の次の行動を遅らせる量（行動ゲージ1000に対して） */
  delay?: number;
  /** 火傷（攻撃力に対する1手番の割合） */
  burn?: number;
  /** 建物の陣材の倍率（突貫普請）・櫓の威力の倍率 */
  costMult?: number;
  towerMult?: number;
  /** 本陣も斬れる（縮地の奥義） */
  core?: boolean;
}

/** 手番を終えない術（建築と同じく、放ったあとに移動・攻撃できる。1手番に1回・待ちあり） */
export const FREE_JUTSU: readonly JutsuKind[] = ['paint', 'lantern', 'raise', 'mend', 'foxfire', 'stance'];

export interface JutsuDef {
  id: string;
  charId: CharId;
  /** 専用ツリーの系統（列 0..2） */
  col: number;
  name: string;
  desc: string;
  kind: JutsuKind;
  /** 待ち手番（strike はキャラの術の値を使う） */
  cd: number;
  /** strike 以外の効果 */
  power?: JutsuPower;
  ougi?: JutsuPower;
  /** 奥義版の説明（相手を狙う術は battle/config.ts の surge.ougi.chars の desc と同じ文。battle.jutsu.test.ts で確かめる） */
  ougiDesc: string;
  /** 奥義の名前（その術の系統の先のスキル「奥義・○○」で覚える） */
  ougiName: string;
}

export const JUTSU: readonly JutsuDef[] = [
  // ---- アカリ（花火・陣取り・守り） ----
  { id: 'hanabi', ougiName: '大輪百花', charId: 'akari', col: 0, name: '花火', desc: '射程3・1.4倍の魔法＋周囲に50%', kind: 'strike', cd: 3, ougiDesc: '術の威力2倍・射程+1・周りにも同じ威力' },
  {
    id: 'okuribi', ougiName: '万灯送り', charId: 'akari', col: 1, name: '送り火', desc: '3マス先までのマスを選び、そのマスと隣を自陣にして、そこの敵に0.6倍の火。手番は終わらない', kind: 'paint', cd: 3,
    power: { radius: 1, range: 3, mult: 0.6 }, ougi: { radius: 2, range: 3, mult: 1.2 }, ougiDesc: '選んだマスから2マス以内、火2倍',
  },
  {
    id: 'toro', ougiName: '千灯の陣', charId: 'akari', col: 2, name: '灯籠陣', desc: '灯籠を連れて歩く陣（4手番）。自分は与ダメ+65%・被ダメ-30%・手番の最初にHP20%回復。周り1マスの自分の建物も被ダメ-30%・威力+65%。手番は終わらない', kind: 'lantern', cd: 3,
    power: { radius: 1, mult: 0, turns: 4, taken: 0.7, dealt: 1.65, heal: 0.2 }, ougi: { radius: 2, mult: 0, turns: 8, taken: 0.7, dealt: 1.65, heal: 0.2 }, ougiDesc: '建物は周り2マスまで、8手番続く',
  },
  // ---- カザマル（速攻・離脱・狩り） ----
  {
    id: 'shukuchi', ougiName: '天翔', charId: 'kazamaru', col: 0, name: '縮地', desc: '直線で3マス先まで跳び（障害物を越える）、着地点の隣の敵1体を1.7倍で斬る', kind: 'leap', cd: 3,
    power: { radius: 3, mult: 1.7 }, ougi: { radius: 6, mult: 3.4, core: true }, ougiDesc: '6マス先まで、3.4倍・本陣も斬れる',
  },
  {
    id: 'tsumuji', ougiName: '大颪', charId: 'kazamaru', col: 1, name: 'つむじ風', desc: '隣の敵をすべて2マス押し出し、2倍のダメージ。押し出した敵の次の行動を遅らせる', kind: 'gust', cd: 3,
    power: { radius: 1, mult: 2, push: 2, delay: 400 }, ougi: { radius: 2, mult: 4, push: 3, delay: 600 }, ougiDesc: '2マス以内、3マス押し出す',
  },
  { id: 'shippu', ougiName: '神風', charId: 'kazamaru', col: 2, name: '疾風', desc: '直線の射程2・1.4倍。直線上の敵と奥の1体まで貫き、次の行動を遅らせる', kind: 'strike', cd: 3, ougiDesc: '術の威力2倍・射程+1・貫通+2・遅らせる量2倍' },
  // ---- スズネ（普請・攻城・守り） ----
  {
    id: 'toppu', ougiName: '一夜城', charId: 'suzune', col: 0, name: '突貫普請', desc: '周りの空きマスに櫓2つと竹柵1つを一気に建てる（陣材半額・建築の回数を使わない・櫓の威力1.5倍）。手番は終わらない', kind: 'raise', cd: 3,
    power: { radius: 1, mult: 0, count: 3, costMult: 0.5, towerMult: 1.5 }, ougi: { radius: 2, mult: 0, count: 6, costMult: 0.5, towerMult: 2 }, ougiDesc: '最大6つ（櫓4・竹柵2）、櫓の威力2倍',
  },
  { id: 'hajo', ougiName: '鬼哭破城', charId: 'suzune', col: 1, name: '破城', desc: '1.3倍＋周囲に30%。本陣へ1.5倍・建物へ2倍', kind: 'strike', cd: 3, ougiDesc: '術の威力2倍・射程+1・本陣へさらに1.5倍' },
  {
    id: 'shuzen', ougiName: '金剛普請', charId: 'suzune', col: 2, name: '鬼の修繕', desc: '2マス以内の自分の建物をHP40%直し（本陣の隣なら本陣も15%）、自分に守り（被ダメ-40%）とHP35%回復。手番は終わらない', kind: 'mend', cd: 3,
    power: { radius: 2, mult: 0, repair: 0.4, coreRepair: 0.15, turns: 1, heal: 0.35 }, ougi: { radius: 99, mult: 0, repair: 0.6, coreRepair: 0.2, turns: 2, heal: 0.7 }, ougiDesc: 'すべての建物をHP60%、本陣を20%直す',
  },
  // ---- ユズハ（迎撃・狐火・決闘） ----
  {
    id: 'gekka', ougiName: '月下無双', charId: 'yuzuha', col: 0, name: '月下の構え', desc: '次の自分の手番まで被ダメ-60%。隣から攻撃してきた相手へ4倍で斬り返す。手番は終わらない', kind: 'stance', cd: 3,
    power: { radius: 0, mult: 0, taken: 0.4, counter: 4 }, ougi: { radius: 0, mult: 0, taken: 0, counter: 5 }, ougiDesc: '攻撃を受けず、5倍で斬り返す',
  },
  {
    id: 'kitsunebijin', ougiName: '狐火大結界', charId: 'yuzuha', col: 1, name: '狐火の陣', desc: '2マス以内に狐火の陣（3手番）。陣の中の敵を燃やして遅らせ、陣で手番を始めた敵も燃える。手番は終わらない', kind: 'foxfire', cd: 3,
    power: { radius: 2, mult: 0, turns: 3, burn: 0.95, delay: 200 }, ougi: { radius: 3, mult: 0, turns: 3, burn: 1.9, delay: 300 }, ougiDesc: '3マス以内、燃え2倍',
  },
  { id: 'kitsunebigiri', ougiName: '九尾一閃', charId: 'yuzuha', col: 2, name: '狐火斬り', desc: '1.45倍の斬撃、与ダメの20%を回復', kind: 'strike', cd: 3, ougiDesc: '術の威力2倍・射程+1・与ダメの50%を回復' },
];

export const JUTSU_BY_ID: Readonly<Record<string, JutsuDef>> = Object.fromEntries(JUTSU.map((j) => [j.id, j]));
/** キャラの術（列の順） */
export const jutsuOf = (charId: CharId): JutsuDef[] => JUTSU.filter((j) => j.charId === charId).sort((a, b) => a.col - b.col);
/** 最初から使える術（キャラの基本の術） */
export const defaultJutsu = (charId: CharId): JutsuDef => JUTSU.find((j) => j.charId === charId && j.kind === 'strike')!;
